import type {
  JSONSchemaConfig,
  JSONSchemaProperty,
} from "@/features/tools/types";
import { Label } from "@/components/ui/label";
import _ from "lodash";
import { ExternalAccountField } from "./external-account-field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useTranslation } from "@/features/utils/i18n";
import { Plus, Trash2, ArrowUpFromLine } from "lucide-react";
import type { ReactNode } from "react";

/**
 * A secret the server keeps sealed at rest (`platform/secrets`): `<version>:` then
 * base64url parts. Mirrors the server's own test, loosely: a value that merely
 * starts like one is shown masked, never sent anywhere else.
 */
const SEALED = /^v\d+:[A-Za-z0-9_-]+:[A-Za-z0-9_-]+:[A-Za-z0-9_-]*$/;

export interface CustomFieldProps {
  fieldName: string;
  fieldSchema: JSONSchemaProperty;
  value: unknown;
  onChange: (value: unknown) => void;
  isRequired?: boolean;
  inheritable?: boolean;
  parentValue?: unknown;
  onToggleInherit?: () => void;
  /**
   * The whole form as the rules see it (an agent's inherited fields filled in
   * from the company), for a field that stands for several — an integration and
   * its account.
   */
  formData?: Record<string, unknown>;
  /** Set several fields in one change, so none of them overwrites another. */
  onChangeFields?: (values: Record<string, unknown>) => void;
}

export interface FieldGroup {
  title?: string;
  description?: string;
  fields: string[];
}

export interface DynamicFormProps {
  schema: JSONSchemaConfig;
  formData: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  path?: string;
  /** Custom renderers for specific fields */
  customFields?: Record<
    string,
    undefined | ((props: CustomFieldProps) => ReactNode)
  >;
  /** Conditional visibility for fields based on form data */
  fieldVisibility?: Record<
    string,
    undefined | ((formData: Record<string, unknown>) => boolean)
  >;
  /** Group fields into sections */
  fieldGroups?: FieldGroup[];
  /** Enable inherit/override functionality for agent overrides */
  inheritable?: boolean;
  /** Parent configuration values to inherit from */
  parentData?: Record<string, unknown>;
}

export function DynamicForm({
  schema,
  formData,
  onChange,
  path = "",
  customFields = {},
  fieldVisibility = {},
  fieldGroups,
  inheritable = false,
  parentData = {},
}: DynamicFormProps) {
  const { t } = useTranslation("tools");

  if (!schema.properties) {
    return null;
  }

  const setIn = (
    data: Record<string, unknown>,
    fieldName: string,
    value: unknown,
  ) => {
    if (!path) {
      data[fieldName] = value;
      return;
    }
    // Handle nested paths
    const pathParts = `${path}.${fieldName}`.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let current: any = data;
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }
    current[pathParts[pathParts.length - 1]] = value;
  };

  const updateFields = (values: Record<string, unknown>) => {
    const newData = { ...formData };
    for (const [fieldName, value] of Object.entries(values)) {
      setIn(newData, fieldName, value);
    }
    onChange(newData);
  };

  const updateField = (fieldName: string, value: unknown) =>
    updateFields({ [fieldName]: value });

  const getFieldValue = (fieldName: string): unknown => {
    if (path) {
      const fullPath = `${path}.${fieldName}`;
      const pathParts = fullPath.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let current: any = formData;
      for (const part of pathParts) {
        if (current === undefined || current === null) return undefined;
        current = current[part];
      }
      return current;
    }
    return formData[fieldName];
  };

  // An agent's field left to inherit is the company's value: that is the one
  // the rules must see, or « integration: internal » inherited would still show
  // the integration key.
  const effectiveData = inheritable
    ? {
        ...parentData,
        ..._.omitBy(formData, (value) => value === undefined),
      }
    : formData;

  const isFieldVisible = (fieldName: string): boolean => {
    const visibilityFn = fieldVisibility[fieldName];
    if (!visibilityFn) return true;
    return visibilityFn(effectiveData);
  };

  const getParentValue = (fieldName: string): unknown => {
    if (path) {
      const fullPath = `${path}.${fieldName}`;
      const pathParts = fullPath.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let current: any = parentData;
      for (const part of pathParts) {
        if (current === undefined || current === null) return undefined;
        current = current[part];
      }
      return current;
    }
    return parentData[fieldName];
  };

  const toggleInherit = (fieldName: string) => {
    const currentValue = getFieldValue(fieldName);
    if (currentValue === undefined) {
      // Currently inheriting, set to parent value or default
      const parentValue = getParentValue(fieldName);
      const defaultValue = schema.properties?.[fieldName]?.default;
      updateField(fieldName, parentValue ?? defaultValue ?? "");
    } else {
      // Currently overriding, set to undefined to inherit
      updateField(fieldName, undefined);
    }
  };

  const renderField = (fieldName: string, fieldSchema: JSONSchemaProperty) => {
    const value = getFieldValue(fieldName);
    const isRequired = schema.required?.includes(fieldName);
    const customRenderer = customFields[fieldName];
    const isInheriting = inheritable && value === undefined;
    const parentValue = inheritable ? getParentValue(fieldName) : undefined;

    if (!isFieldVisible(fieldName)) {
      return null;
    }

    // A schema can ask for an account picker without every caller having to
    // wire a custom field for it.
    if (!customRenderer && fieldSchema.format === "external_account") {
      return (
        <ExternalAccountField
          fieldName={fieldName}
          fieldSchema={fieldSchema}
          value={value}
          onChange={(newValue) => updateField(fieldName, newValue)}
          isRequired={isRequired}
          inheritable={inheritable}
          parentValue={parentValue}
        />
      );
    }

    if (customRenderer) {
      return customRenderer({
        fieldName,
        fieldSchema,
        value,
        onChange: (newValue) => updateField(fieldName, newValue),
        isRequired,
        inheritable,
        parentValue,
        onToggleInherit: () => toggleInherit(fieldName),
        formData: effectiveData,
        onChangeFields: updateFields,
      });
    }

    return (
      <DynamicField
        fieldName={fieldName}
        fieldSchema={fieldSchema}
        value={value}
        onChange={(newValue) => updateField(fieldName, newValue)}
        isRequired={isRequired}
        inheritable={inheritable}
        isInheriting={isInheriting}
        parentValue={parentValue}
        onToggleInherit={() => toggleInherit(fieldName)}
      />
    );
  };

  // If field groups are defined, render grouped fields
  if (fieldGroups && fieldGroups.length > 0) {
    const allGroupedFields = new Set(
      fieldGroups.flatMap((group) => group.fields),
    );
    const ungroupedFields = Object.keys(schema.properties).filter(
      (field) => !allGroupedFields.has(field),
    );

    return (
      <div className="space-y-6">
        {/* Render grouped fields */}
        {fieldGroups.map((group, index) => {
          const visibleFields = group.fields.filter(
            (fieldName) =>
              schema.properties[fieldName] && isFieldVisible(fieldName),
          );

          if (visibleFields.length === 0) {
            return null;
          }

          return (
            <Card key={index}>
              {group.title && (
                <CardHeader>
                  <CardTitle>{group.title}</CardTitle>
                  {group.description && (
                    <CardDescription>{group.description}</CardDescription>
                  )}
                </CardHeader>
              )}
              <CardContent className="space-y-4">
                {visibleFields.map((fieldName) => (
                  <div key={fieldName}>
                    {renderField(fieldName, schema.properties[fieldName])}
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}

        {/* Render ungrouped fields */}
        {ungroupedFields.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("tools.form.otherSettings")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ungroupedFields.map((fieldName) => (
                <div key={fieldName}>
                  {renderField(fieldName, schema.properties[fieldName])}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Default rendering without groups
  return (
    <div className="space-y-4">
      {Object.entries(schema.properties).map(([fieldName, fieldSchema]) => (
        <div key={fieldName}>{renderField(fieldName, fieldSchema)}</div>
      ))}
    </div>
  );
}

interface DynamicFieldProps {
  fieldName: string;
  fieldSchema: JSONSchemaProperty;
  value: unknown;
  onChange: (value: unknown) => void;
  isRequired?: boolean;
  inheritable?: boolean;
  isInheriting?: boolean;
  parentValue?: unknown;
  onToggleInherit?: () => void;
}

function DynamicField({
  fieldName,
  fieldSchema,
  value,
  onChange,
  isRequired,
  inheritable = false,
  isInheriting = false,
  parentValue,
  onToggleInherit,
}: DynamicFieldProps) {
  const { t } = useTranslation("tools");

  const label = fieldSchema.title || fieldName;
  const description = fieldSchema.description;

  // Format parent value for display
  const formatValue = (val: unknown): string => {
    if (val === undefined || val === null) return t("tools.form.notSet");
    if (typeof val === "boolean")
      return val ? t("tools.form.yes") : t("tools.form.no");
    if (Array.isArray(val)) return t("tools.form.items", { count: val.length });
    if (typeof val === "object") return t("tools.form.complexValue");
    // An enum inherits its label, not the raw value ("direct_transfer").
    const option = fieldSchema.enum?.indexOf(val as string | number) ?? -1;
    if (option >= 0 && fieldSchema.enumLabels?.[option]) {
      return fieldSchema.enumLabels[option];
    }
    return String(val);
  };

  // Render inherit/override toggle
  const renderInheritToggle = () => {
    if (!inheritable || !onToggleInherit) return null;

    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onToggleInherit}
        className="h-6 px-2 text-xs"
      >
        {isInheriting ? (
          <>
            <ArrowUpFromLine className="w-3 h-3 mr-1" />
            {t("tools.form.override")}
          </>
        ) : (
          <>
            <ArrowUpFromLine className="w-3 h-3 mr-1" />
            {t("tools.form.inherit")}
          </>
        )}
      </Button>
    );
  };

  // If inheriting, show collapsed view
  if (isInheriting) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderInheritToggle()}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/50 rounded-md border border-dashed">
          <ArrowUpFromLine className="w-4 h-4" />
          <span>
            {t("tools.form.inheriting")}{" "}
            <strong>{formatValue(parentValue)}</strong>
          </span>
        </div>
      </div>
    );
  }

  // Array type
  if (fieldSchema.type === "array") {
    const arrayValue = Array.isArray(value) ? value : [];

    const addItem = () => {
      const newItem =
        fieldSchema.items?.type === "object"
          ? {}
          : fieldSchema.items?.default || "";
      onChange([...arrayValue, newItem]);
    };

    const removeItem = (index: number) => {
      const newArray = arrayValue.filter((_, i) => i !== index);
      onChange(newArray);
    };

    const updateItem = (index: number, newValue: unknown) => {
      const newArray = [...arrayValue];
      newArray[index] = newValue;
      onChange(newArray);
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderInheritToggle()}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}

        <div className="space-y-2 pl-4 border-l-2 border-border">
          {arrayValue.map((item, index) => (
            <div key={index} className="flex gap-2">
              <div className="flex-1">
                {fieldSchema.items?.type === "object" &&
                fieldSchema.items.properties ? (
                  <div className="p-3 border rounded-lg space-y-3">
                    {Object.entries(fieldSchema.items.properties).map(
                      ([subFieldName, subFieldSchema]) => (
                        <DynamicField
                          key={subFieldName}
                          fieldName={subFieldName}
                          fieldSchema={subFieldSchema as JSONSchemaProperty}
                          value={item[subFieldName]}
                          onChange={(newValue) => {
                            const newItem = {
                              ...item,
                              [subFieldName]: newValue,
                            };
                            updateItem(index, newItem);
                          }}
                        />
                      ),
                    )}
                  </div>
                ) : (
                  <Input
                    value={item}
                    onChange={(e) => updateItem(index, e.target.value)}
                  />
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeItem(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={addItem}>
          <Plus className="w-4 h-4 mr-2" />
          {fieldSchema.addLabel || t("tools.form.add", { label })}
        </Button>
      </div>
    );
  }

  // Object type
  if (fieldSchema.type === "object" && fieldSchema.properties) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderInheritToggle()}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        <div className="p-4 border rounded-lg space-y-3">
          {Object.entries(fieldSchema.properties).map(
            ([subFieldName, subFieldSchema]) => (
              <DynamicField
                key={subFieldName}
                fieldName={subFieldName}
                fieldSchema={subFieldSchema as JSONSchemaProperty}
                value={
                  (value as Record<string, unknown> | undefined)?.[subFieldName]
                }
                onChange={(newValue) => {
                  onChange({
                    ...((value as Record<string, unknown>) || {}),
                    [subFieldName]: newValue,
                  });
                }}
              />
            ),
          )}
        </div>
      </div>
    );
  }

  // Enum/Select type
  if (fieldSchema.enum) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={fieldName}>
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderInheritToggle()}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        <Select value={value?.toString() || ""} onValueChange={onChange}>
          <SelectTrigger id={fieldName}>
            <SelectValue placeholder={t("tools.form.select", { label })} />
          </SelectTrigger>
          <SelectContent>
            {fieldSchema.enum.map((option, index) => (
              <SelectItem
                key={option?.toString() || index}
                value={option?.toString() || ""}
              >
                {fieldSchema.enumLabels?.[index] || option?.toString()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Boolean type. Overriding for an agent is the third state: the inherit
  // toggle above decides whether the switch shows at all.
  if (fieldSchema.type === "boolean") {
    return (
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor={fieldName}>
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {renderInheritToggle()}
          <Switch
            id={fieldName}
            checked={value === true}
            onCheckedChange={(checked) => onChange(checked)}
          />
        </div>
      </div>
    );
  }

  // String type with textarea
  if (fieldSchema.type === "string" && fieldSchema.format === "textarea") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={fieldName}>
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderInheritToggle()}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        <Textarea
          id={fieldName}
          value={String(value || "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            fieldSchema.default ? String(fieldSchema.default) : undefined
          }
          rows={4}
        />
      </div>
    );
  }

  // Number type
  if (fieldSchema.type === "number") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={fieldName}>
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderInheritToggle()}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        <Input
          id={fieldName}
          type="number"
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) =>
            onChange(e.target.value ? Number(e.target.value) : undefined)
          }
          placeholder={
            fieldSchema.default ? String(fieldSchema.default) : undefined
          }
          min={fieldSchema.minimum}
          max={fieldSchema.maximum}
        />
      </div>
    );
  }

  // A secret the server keeps sealed (a custom API's or an MCP server's header
  // value): it is never shown, and left untouched it is saved back as it came.
  const sealed = typeof value === "string" && SEALED.test(value);

  // Default: String input
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={fieldName}>
          {label}
          {isRequired && <span className="text-destructive ml-1">*</span>}
        </Label>
        {renderInheritToggle()}
      </div>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      <Input
        id={fieldName}
        type={
          fieldSchema.format === "password" || sealed
            ? "password"
            : fieldSchema.format === "email" ||
                fieldSchema.format === "url" ||
                fieldSchema.format === "tel"
              ? fieldSchema.format
              : "text"
        }
        // A third party's secret: the browser must not fill in the user's own
        // HeyUp password, nor offer to save this one as theirs.
        autoComplete={
          fieldSchema.format === "password" || sealed
            ? "new-password"
            : undefined
        }
        value={sealed ? "" : String(value || "")}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          sealed
            ? t("tools.form.sealedSecret")
            : fieldSchema.default
              ? String(fieldSchema.default)
              : undefined
        }
        minLength={fieldSchema.minLength}
        maxLength={fieldSchema.maxLength}
        pattern={fieldSchema.pattern}
      />
    </div>
  );
}
